import { Injectable, Renderer2, signal } from '@angular/core';
import {
  DIRECTIONS,
  DOCUMENTS_PARSE_MAP,
  POSTAL_TYPE,
  resetDirections,
  resetPostalTypes,
  setDirections,
  setDocuments,
  setPostalTypes,
} from '../metadata';
import {
  Batch,
  BatchDepartureResp,
  BatchMailing,
  BatchParseDto,
  BatchParseItem,
  ListCreatingOptions,
  PartialReceiptsItem,
  PartialReceiptsItemWithSelected,
  RecipientObject,
} from '../models';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { parseInt } from 'lodash-es';
import { document } from 'ngx-bootstrap/utils';
import { BlankStatusType } from '$modules/personal/modules/batch/features/blanks/blanks.metadata';
import { LabelSizeList } from '../models';
import { createIdleState, RequestState } from '$core/util';
import { GenerateOneBatchDocumentEventParams } from './batches/generate-batch-document-params';

@Injectable()
export class BatchesParseStorageService {
  public get batchDepartureAttachments$(): BehaviorSubject<
    PartialReceiptsItemWithSelected[]
  > {
    return this._batchDepartureAttachments$;
  }

  public set batchDepartureAttachments(value: PartialReceiptsItem[]) {
    this._batchDepartureAttachments$.next(
      value.map(
        (attachment) =>
          ({
            ...attachment,
            selected: false,
          }) as PartialReceiptsItemWithSelected
      )
    );
  }

  public departureBlankStatus$ = new BehaviorSubject<BlankStatusType>('new');
  public batchDeparture$ = new BehaviorSubject<BatchMailing | null>(null);
  private _batchDepartureAttachments$ = new BehaviorSubject<
    PartialReceiptsItemWithSelected[]
  >(null);
  private _isFullScreen$ = new BehaviorSubject<boolean>(false);

  public readonly batchItems = {
    valid: [],
    invalid: [],
  };

  public get isFullScreen$(): BehaviorSubject<boolean> {
    return this._isFullScreen$;
  }

  /**
   * Label sizes for all batches with any postal delivery types.
   */
  public readonly labelSizes = signal<LabelSizeList | null>(null);

  /**
   * Selected label size for the particular batch.
   */
  public readonly selectedLabelSize = signal<string | null>(null);

  /**
   * Departures of the viewed batch.
   */
  public readonly batchDepartures = signal<BatchDepartureResp | null>(null);

  /**
   * The state of the label size update request.
   */
  public readonly labelSizeUpdateRequestState =
    signal<RequestState<Batch>>(createIdleState());

  /**
   * Subject to emit the data of the departure document to be generated.
   */
  public readonly generateDepartureDocument$ =
    new Subject<GenerateOneBatchDocumentEventParams>();

  constructor(private readonly translateService: TranslateService) {}

  public reset(): void {
    this.batchItems.valid = [];
    this.batchItems.invalid = [];
    resetDirections();
    resetPostalTypes();
  }

  public adapter(dto: BatchParseDto) {
    this.createBatchAdapter(dto.list_creating_options);
    this.createDepartureAdapter(dto.items);
  }

  private createDepartureAdapter(creationDto: BatchParseItem[]): void {
    if (creationDto.length) {
      creationDto.forEach((el) => {
        const recipientObj = this.transformRecipientObject(
          el.recipient_object,
          el.is_address_valid
        );
        const data = {
          ...el,
          ...el.addons,
          recipient_object: recipientObj,
          recipient_id: {
            value: el.recipient_id ? el.recipient_id.value : '0',
            error: recipientObj ? recipientObj.error : null,
          },
        };

        if (el.is_item_valid && el.is_address_valid) {
          this.batchItems.valid.push(data);
        } else {
          this.batchItems.invalid.push(data);
        }
      });
    }
  }

  private createBatchAdapter(creationDto: ListCreatingOptions): void {
    if (creationDto.direction.length) {
      setDirections(
        DIRECTIONS.filter((el) => creationDto.direction.includes(el.value))
      );
    } else {
      setDirections(DIRECTIONS);
    }

    if (creationDto.postal_type.length) {
      setPostalTypes(
        POSTAL_TYPE.filter((el) =>
          creationDto.postal_delivery_type.includes(el.value)
        )
      );
    } else {
      setPostalTypes(POSTAL_TYPE);
    }

    setDocuments(
      creationDto.postal_type.map((el) => DOCUMENTS_PARSE_MAP.get(el))
    );
  }

  private transformRecipientObject(
    originalObject: RecipientObject,
    valid?: boolean
  ) {
    const errorsSet = new Set();
    const result = {};

    for (const [key, { value, error }] of Object.entries(originalObject)) {
      result[key] = value;

      result['full_address'] = `${
        result['postcode'] ? result['postcode'] + ', ' : ''
      }${
        result['region']
          ? result['region'] +
            ' ' +
            this.translateService.instant('REGION_UPPER') +
            ', '
          : ''
      }${
        result['district']
          ? result['district'] +
            ' ' +
            this.translateService.instant('DIST_UPPER') +
            ', '
          : ''
      }${result['city_type'] ? result['city_type'] + ' ' : ''}${
        result['city'] ? result['city'] + ', ' : ''
      }${result['street_type'] ? result['street_type'] + ' ' : ''}${
        result['street'] ? result['street'] : ''
      }${
        result['building']
          ? ', ' +
            this.translateService.instant('HOUSE_UPPER') +
            ' ' +
            result['building']
          : ''
      }${
        result['apartment']
          ? ', ' +
            this.translateService.instant('APPART_UPPER') +
            ' ' +
            result['apartment']
          : ''
      }`;

      if (!valid && !error) {
        errorsSet.add('BATCH_MAILING.BATCHES_TAB.CREATION.WRONG_ADDRESS');
      }

      if (error) {
        errorsSet.add(error);
      }
    }

    return {
      error: Array.from(errorsSet),
      value: {
        ...result,
        address: result,
      },
    };
  }

  public generateEMSTimePeriod(
    is_district_center?: boolean,
    city?: string
  ): string[] {
    let endTime = '20:00'; // По умолчанию
    if (city) {
      city = city.toLowerCase();
    }
    const valid_non_regional_center =
      city === 'жодино' || city === 'новополоцк';
    const is_regional_center =
      // обласной центр
      city === 'минск' ||
      city === 'гомель' ||
      city === 'брест' ||
      city === 'гродно' ||
      city === 'витебск' ||
      city === 'могилев';

    if (
      (is_district_center || valid_non_regional_center) &&
      !is_regional_center
    ) {
      endTime = '19:00'; // Для районных центров, кроме областных центров
    } else if (
      city === 'могилев' ||
      city === 'гомель' ||
      city === 'брест' ||
      city === 'гродно' ||
      city === 'витебск'
    ) {
      endTime = '21:00'; // Для других областных центров, кроме Минска
    } else if (city === 'минск') {
      endTime = '22:00'; // Для Минска
    }

    const startTime = '10:00';
    const timePeriodArr = [];
    let currentTime = startTime;

    while (currentTime <= endTime) {
      timePeriodArr.push(currentTime);
      const [hours, minutes] = currentTime.split(':');
      const totalMinutes =
        parseInt(hours, 10) * 60 + parseInt(minutes, 10) + 30;
      const newHours = Math.floor(totalMinutes / 60);
      const newMinutes = totalMinutes % 60;
      currentTime = `${newHours.toString().padStart(2, '0')}:${newMinutes
        .toString()
        .padStart(2, '0')}`;
    }

    return timePeriodArr;
  }

  public toggleFullScreen(renderer: Renderer2) {
    const currentFullScreenState = this._isFullScreen$.getValue();
    this._isFullScreen$.next(!currentFullScreenState);

    const body = document.body;
    const jivositeStyles =
      'b24-widget-button-wrapper b24-widget-button-position-bottom-right b24-widget-button-visible';
    const jivosite = document.getElementsByClassName(jivositeStyles);

    if (!currentFullScreenState) {
      // Полноэкранный режим включен
      renderer.setStyle(body, 'overflow', 'hidden');
      if (jivosite.length) {
        renderer.setStyle(jivosite[0], 'display', 'none');
      }
    } else {
      // Полноэкранный режим выключен
      renderer.removeStyle(body, 'overflow');
      if (jivosite.length) {
        renderer.removeStyle(jivosite[0], 'display');
      }
    }
  }
}
